import CloseIcon from '@mui/icons-material/Close';
import { IconButton, List, ListSubheader, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { useAuthState } from 'state/useAuthState';
import { useMapLayersState } from 'state/useMapLayersState';
import { usePoisState } from 'state/usePoisState';
import { useWebGL2State } from 'state/useWebGL2State';
import { makeStyles } from 'tss-react/mui';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import {
  MapLayersDetails,
  MapLayer,
  MapLayerDetail,
} from 'features/mapping/mapping.constants';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import {
  layerNameToEntityType,
  layerNameToPoiType,
  canUserAccessMapLayer,
  getMapLayerPaywallLink,
  trackMapLayerToggleToPosthog,
  getVisibleGroupLayers,
  type MapLayerAccessContext,
} from 'features/mapping/mapping.utils';
import { LayerGroupControlItem } from './LayerGroupControlItem';

const useStyles = makeStyles()(() => ({
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
  },
}));

export const DrawerGroupContent = (): JSX.Element | null => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { mapLayers, setMapLayerVisibility, disabledLayers } =
    useMapLayersState();
  const { hasWebGL2 } = useWebGL2State();
  const history = useHistory();
  const { selectedPoi, clearSelectedPoisOfType } = usePoisState();
  const { mapDrawerEntityForPoi, setMapDrawerEntityForPoi } =
    useMapDrawerEntityForPoiState();
  const { showMembershipFeatures, showMembershipProFeatures, permissions } =
    useAuthState();
  const { group, closeLayersGroup } = useLayersDrawerState();
  const isHeatDetectionsGisEnabled = useReleaseFlag(
    'show_heat_detections_from_gis_in_frontend',
  );
  const isNgfsDetectionsEnabled = useReleaseFlag(
    'show_ngfs_detections_in_frontend',
  );

  const layerAccess: MapLayerAccessContext = {
    showMembershipFeatures,
    showMembershipProFeatures,
    permissions,
  };

  const handleChangeLayer = (
    mapLayer: MapLayer,
    details: MapLayerDetail,
    enabled: boolean,
  ): void => {
    const paywallLink = getMapLayerPaywallLink(mapLayer, details, layerAccess);
    if (paywallLink) {
      history.push(paywallLink, {
        fromRoute: window.location.pathname + window.location.search,
      });
      return;
    }

    setMapLayerVisibility(mapLayer, enabled);
    trackMapLayerToggleToPosthog(mapLayer, enabled);
    if (!enabled) {
      if (selectedPoi && layerNameToPoiType[mapLayer]) {
        clearSelectedPoisOfType(layerNameToPoiType[mapLayer]);
      }
      if (layerNameToEntityType[mapLayer] === mapDrawerEntityForPoi?.type) {
        setMapDrawerEntityForPoi(null);
      }
    }
  };

  const renderControlItem = (mapLayer: MapLayer): JSX.Element | null => {
    const layerDetails: MapLayerDetail | undefined = MapLayersDetails[mapLayer];

    if (!layerDetails?.imageSrc || !('titleKey' in layerDetails)) return null;

    const disabled =
      (layerDetails.requiresWebGL2 && !hasWebGL2) ||
      disabledLayers.includes(mapLayer);

    const checked =
      !disabled &&
      mapLayers.includes(mapLayer) &&
      canUserAccessMapLayer({
        minMembership: layerDetails.minMembership,
        requiredPermissions: layerDetails.requiredPermissions,
        ...layerAccess,
      });

    return (
      <LayerGroupControlItem
        key={mapLayer}
        imageSrc={layerDetails.imageSrc}
        title={t(layerDetails.titleKey)}
        subtitle={t(layerDetails.subtitleKey)}
        disabled={disabled}
        checked={checked}
        onChange={(enabled): void =>
          handleChangeLayer(mapLayer, layerDetails, enabled)
        }
        mapLayer={mapLayer}
        internalOnly={layerDetails.internalOnly}
        experimental={layerDetails.experimental}
        minMembership={layerDetails.minMembership}
      />
    );
  };

  if (!group) {
    return null;
  }

  const visibleLayers = getVisibleGroupLayers(
    group,
    isHeatDetectionsGisEnabled,
    isNgfsDetectionsEnabled,
  );

  return (
    <List
      subheader={
        <ListSubheader
          disableGutters
          sx={{ lineHeight: 1.2, padding: 2, paddingBottom: 0 }}
        >
          <Typography
            variant="h3"
            color="textPrimary"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {t(`map.layers.general.${group}.title`)}
          </Typography>
          <IconButton
            aria-label={t('common.close')}
            className={classes.closeButton}
            onClick={closeLayersGroup}
            size="large"
            sx={{ color: 'text.primary' }}
          >
            <CloseIcon />
          </IconButton>
        </ListSubheader>
      }
    >
      {visibleLayers.map(renderControlItem)}
    </List>
  );
};
